// Which steps of a flow apply right now. Pure: the product computes the
// context (surface + named boolean facts); the JSON only names facts.

import type { FlowModel, StepNode, Surface } from "./flow-model";

export type FlowContext = {
  surface: Surface;
  /** Named booleans the product computes. An unknown fact is false. */
  facts?: Readonly<Record<string, boolean>>;
};

/** A kind's capability: the surfaces it can work on at all. */
export type KindSurfaces = (kind: string) => readonly Surface[] | null;

function asList<T>(value: T | T[] | undefined): T[] {
  if (value == null) return [];
  return Array.isArray(value) ? value : [value];
}

export function stepApplies(
  step: StepNode,
  ctx: FlowContext,
  kindSurfaces: KindSurfaces = () => null,
): boolean {
  const capability = kindSurfaces(step.kind);
  if (capability != null && !capability.includes(ctx.surface)) return false;

  const gate = step.gate;
  if (gate == null) return true;

  const surfaces = asList(gate.surface);
  if (surfaces.length > 0 && !surfaces.includes(ctx.surface)) return false;

  const facts = ctx.facts ?? {};
  if (!asList(gate.when).every((f) => facts[f] === true)) return false;
  if (asList(gate.unless).some((f) => facts[f] === true)) return false;
  return true;
}

/** The active steps, in model order. */
export function resolveSteps(
  model: FlowModel,
  ctx: FlowContext,
  kindSurfaces?: KindSurfaces,
): StepNode[] {
  return model.steps.filter((s) => stepApplies(s, ctx, kindSurfaces));
}
